<template>
  <div>
    <organism-ticket-management
      v-if="!isLoading && filterData"
      :filter-list="filterData"
      :status-option="statusOption"
      :fields="fields"
      :items="ticketList"
      @clickRow="clickRow"
      @onUpdateFilterData="onUpdateFilterData"
      @onUpdateCurrentPageOrPerPage="onUpdateCurrentPageOrPerPage"
      @onSetDefaultFilterData="onSetDefaultFilterData"
      @clickDelete="clickDeleteTicket"
    ></organism-ticket-management>
  </div>
</template>

<script>
import { DateTime } from "luxon"
import {
  getExternalTicketList,
  deleteExternalTicketById,
} from "@/api/api-module1"
import { mapActions, mapGetters } from "vuex"
import mixins from "@/mixins"
import OrganismTicketManagement from "@/components/organisms/portal/external/OrganismTicketManagement.vue"
import mixinsObjectHelper from "@/mixins/mixinsObjectHelper"
import mixinsDatetime from "@/mixins/mixinsDatetime"

export default {
  name: "TicketManagement",
  components: { OrganismTicketManagement },
  mixins: [mixins, mixinsObjectHelper, mixinsDatetime],
  data() {
    return {
      statusOption: [
        { id: "OPENED", name: "Opened" },
        { id: "IN_PROGRESS", name: "In Progress" },
        { id: "CLOSED", name: "Closed" },
      ],

      fields: [
        {
          key: "status",
          label: "STATUS",
          thClass: "w-100px",
          tdClass: "w-100px",
        },
        {
          key: "id",
          label: "Ticket ID",
          thClass: "w-120px",
          tdClass: "w-120px",
        },
        {
          key: "content",
          label: "SUBJECT/DETAIL",
          thClass: "w-300px",
          tdClass: "w-300px",
        },
        {
          key: "createdDate",
          label: "CREATED DATE",
          thClass: "w-150px",
          tdClass: "w-150px",
        },
        {
          key: "lastUpdated",
          label: "LAST UPDATED",

          thClass: "w-150px",
          tdClass: "w-150px",
        },
        {
          key: "communication",
          label: "COMMUNICATION",
          thClass: "w-200px",
          tdClass: "w-200px",
        },
        {
          key: "deleteIcon",
          label: "",
          thClass: "w-50px",
          tdClass: "w-50px",
        },
      ],
      ticketList: [],
      filterData: null,
    }
  },
  computed: {
    ...mapGetters("appFilter", ["getExternalTicketFilter"]),

    isLoading() {
      return !this.ticketList
    },
  },
  async mounted() {
    await this.getDataFromApi()
  },
  methods: {
    ...mapActions("appFilter", ["setExternalTicketFilter"]),

    cloneFilterDataFromVuex() {
      const filterDataFromVuex = this.getExternalTicketFilter
      this.filterData = this.deepCopyObject(filterDataFromVuex)
    },

    // get data from api
    async getDataFromApi() {
      await this.getTicketListFromApi()
    },

    // get ticket list
    async getTicketListFromApi() {
      this.showLoading(true)
      this.cloneFilterDataFromVuex()
      const filterData = this.filterDecoration()
      await getExternalTicketList(filterData)
        .then((response) => {
          this.filterData.totalCount = response.totalCount
          const updatedFilter = {
            ...this.getExternalTicketFilter,
            totalCount: response.totalCount,
          }
          this.setExternalTicketFilter(updatedFilter)
          this.ticketList = response.data.map((ticket) => {
            return {
              status: ticket.status,
              id: ticket.ticketId,
              createdDate: {
                date: DateTime.fromISO(ticket.openedAt).toFormat("dd/MM/yyyy"),
                time: DateTime.fromISO(ticket.openedAt).toFormat("HH:mm:ss"),
              },
              content: {
                subject: ticket.ticketSubject,
                detail: ticket.ticketDetail,
              },
              lastUpdated: {
                date: DateTime.fromISO(ticket.updatedAt).toFormat("dd/MM/yyyy"),
                time: DateTime.fromISO(ticket.updatedAt).toFormat("HH:mm:ss"),
              },
              communication: {
                createdBy: ticket.lastReply ? ticket.lastReply.createdBy : null,
                createdAt: DateTime.fromISO(
                  ticket.lastReply?.createdAt
                ).toFormat("dd/MM/yyyy HH:mm:ss"),
                threadMessage: ticket.lastReply?.threadMessage,
              },
            }
          })
        })
        .finally(() => this.showLoading(false))
    },

    filterDecoration() {
      const filter = {
        PageSize: this.filterData.perPage,
        PageNumber: this.filterData.currentPage,
        TicketId: this.filterData.ticketId || undefined,
        StartDate: this.filterData.isEnableDate
          ? this.removeTimeZone(this.filterData.dateTime.start)
          : undefined,
        EndDate: this.filterData.isEnableDate
          ? this.removeTimeZone(this.filterData.dateTime.end)
          : undefined,
        Search: this.filterData.subjectDetail || undefined,
        Status:
          this.filterData.status.map((item) => item.id).join(",") || undefined,
      }
      return filter
    },

    // click filter
    onUpdateFilterData(filter) {
      filter.currentPage = 1
      this.setExternalTicketFilter(filter)
      this.getTicketListFromApi()
    },

    onUpdateCurrentPageOrPerPage(filter) {
      const updatedFilter = {
        ...this.getExternalTicketFilter,
        currentPage: filter.currentPage,
        perPage: filter.perPage,
      }
      this.setExternalTicketFilter(updatedFilter)
      this.getTicketListFromApi()
    },

    onSetDefaultFilterData() {
      const defaultFilter = {
        status: [
          { id: "OPENED", name: "Opened" },
          { id: "IN_PROGRESS", name: "In Progress" },
        ],
        ticketId: "",
        subjectDetail: "",
        dateTime: {
          start: DateTime.now().startOf("day").toISO(),
          end: DateTime.now().toISO(),
          now: DateTime.now().toISO(),
        },
        isEnableDate: false,
        currentPage: 1,
        totalCount: 0,
        perPage: 10,
      }
      this.setExternalTicketFilter(defaultFilter)
      this.getTicketListFromApi()
    },

    clickRow(record, index) {
      this.$router.push(`/web-portal/ticket/${record.id}`)
    },

    // click delete
    async clickDeleteTicket(ticketId) {
      this.showLoading(true)
      await deleteExternalTicketById(ticketId)
        .then((response) => {
          if (response.status === "ok") {
            this.toastSuccess("Ticket deleted successfully")

            this.getDataFromApi()
          } else this.toastError("Ticket deleted failed")
        })
        .finally(() => this.showLoading(false))
    },
  },
}
</script>

<style lang="scss" scoped></style>
